import type { FC } from 'react'
import { createRef } from 'react'

import { getMimeTypeExtensionMap } from '@common/utils/file'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'

import { AgencyLogoUploadField } from './AgencyLogoUploadField'

interface Props {
  isOpen: boolean
  onClose: () => void
  onReset?: () => void
  onSelect?: (props: { ligthMode: Blob | null; darkMode: Blob | null }) => void
  selectedLightMode: string | undefined
  selectedDarkMode: string | undefined
  setSelectedLightMode: (selected: string | undefined) => void
  setSelectedDarkMode: (selected: string | undefined) => void
}

const ACCEPT = getMimeTypeExtensionMap({
  types: ['image'],
  subTypes: { image: ['png', 'jpeg'] },
})

export const AgencyLogoSelector: FC<Props> = ({
  isOpen,
  onClose,
  onSelect,
  onReset,
  selectedLightMode,
  selectedDarkMode,
  setSelectedLightMode,
  setSelectedDarkMode,
}) => {
  const lightModeEditor = createRef<Blob | null>()
  const darkModeEditor = createRef<Blob | null>()

  const isSelected = selectedLightMode && selectedDarkMode

  const handleReset = () => {
    setSelectedDarkMode(undefined)
    setSelectedLightMode(undefined)
    onReset?.()
  }

  const onConfirm = () => {
    if (onSelect && lightModeEditor.current && darkModeEditor.current) {
      const lightModeBlob = lightModeEditor.current
      const darkModeBlob = darkModeEditor.current

      onSelect({ ligthMode: lightModeBlob, darkMode: darkModeBlob })
    }
  }

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      panelClassName="!max-w-fit"
    >
      <Dialog.Title className="block text-center">
        Select a new Agency Logo
      </Dialog.Title>
      <Dialog.Description className="mb-4 mt-2 block text-center">
        We support PNG & JPG under 3MB with size 150x25 (Recommended PNG).
      </Dialog.Description>
      <div className="flex gap-2">
        <AgencyLogoUploadField
          accept={ACCEPT}
          ref={lightModeEditor}
          selected={selectedLightMode}
          onSelect={setSelectedLightMode}
          title="Light Mode"
          mode="light"
        />
        <AgencyLogoUploadField
          accept={ACCEPT}
          ref={darkModeEditor}
          selected={selectedDarkMode}
          onSelect={setSelectedDarkMode}
          title="Dark Mode"
          mode="dark"
        />
      </div>
      <Dialog.Footer>
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          className="flex flex-1 items-center gap-2"
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleReset}
          className="flex flex-1 items-center gap-2"
        >
          Reset
        </Button>
        <Button
          disabled={!isSelected}
          onClick={onConfirm}
          className="flex flex-1 items-center gap-2"
        >
          Select
        </Button>
      </Dialog.Footer>
    </Dialog>
  )
}
